import { Component, inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { HttpErrorResponse }                                       from '@angular/common/http';
import { ReactiveFormsModule, UntypedFormBuilder, Validators }     from '@angular/forms';
import { MatError, MatFormField, MatFormFieldModule, MatLabel }    from '@angular/material/form-field';
import { MatInput }                                                from '@angular/material/input';
import { ActivatedRoute, Router }                                  from '@angular/router';
import { MatProgressSpinner }                                      from '@angular/material/progress-spinner';

import { RegistrationSideComponent }   from './registration-side/registration-side.component';
import { SessionService }              from '../../../api/session/session.service';
import { ComponentType }               from '../../../shared/components/components.utils';
import { ButtonComponent, ButtonType } from '../../../shared/components/buttons/button.component';
import { AuthenticationService }       from '../../../core/auth/authentication.service';
import { LogoComponent }               from '../../../shared/components/logo/logo.component';
import { PinNotificationComponent }    from '../../../shared/components/notification/notification.component';
import { PasswordInputComponent }      from '../../../shared/components/password-input/password-input.component';
import { FooterComponent }             from '../../../shared/components/footer/footer.component';
import { InternalRoutes }              from '../../../shared/data/internal-routes';
import { ErrorService }                from '../../../core/error-handling/error.service';

const PASSWORDLESS_SETUP_REQUIRED = 'PASSWORDLESS_SETUP_REQUIRED';
const DUPLICATE_USERNAME_SETUP_REQUIRED = 'DUPLICATE_USERNAME_SETUP_REQUIRED';

enum LoginPageNotification {
  CodeExpired = 'CodeExpired',
  LimitExceededException = 'LimitExceededException',
  ForgotPasswordChangeSuccess = 'ForgotPasswordChangeSuccess',
  RegistrationNotConfirmed = 'RegistrationNotConfirmed',
  ResendConfirmationMailSuccess = 'ResendConfirmationMailSuccess',
  SessionExpired = 'SessionExpired',
  TooManyLoginAttempts = 'TooManyLoginAttempts',
  WrongUsernameOrPassword = 'WrongUsernameOrPassword',
  UserProfilePasswordChangeSuccess = 'UserProfilePasswordChangeSuccess',
  TryAgainLater = 'TryAgainLater',
  UsernameRemediationSuccess = 'UsernameRemediationSuccess'
}

@Component({
  selector: 'pin-login-page',
  templateUrl: './login-page.component.html',
  styleUrls: ['./login-page.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [LogoComponent, PinNotificationComponent, ButtonComponent, ReactiveFormsModule, MatFormField, MatLabel, MatInput, MatError, MatFormFieldModule, PasswordInputComponent, RegistrationSideComponent, FooterComponent, MatProgressSpinner, PasswordInputComponent]
})
export class LoginPageComponent implements OnInit, OnDestroy {
  notificationEnum = LoginPageNotification;
  isLoading = false;
  resendingMail = false;
  notification: LoginPageNotification;
  logoType = ComponentType;
  buttonType = ButtonType;
  private authService = inject(AuthenticationService);
  private sessionService = inject(SessionService);
  private route = inject(ActivatedRoute);
  private formBuilder = inject(UntypedFormBuilder);
  public loginForm = this.formBuilder.group({
    username: [null, [Validators.required]],
    password: [null, [Validators.required]]
  });
  private router = inject(Router);
  private errorService = inject(ErrorService);
  private username: string;
  private returnUrl: string;
  private resendTimeout: ReturnType<typeof setTimeout>;

  ngOnInit() {
    const queryParams = this.route.snapshot.queryParamMap;
    this.returnUrl = queryParams.has('returnUrl') ? queryParams.get('returnUrl') : '/';

    this.checkAndDisplayNotifications();
  }

  login() {
    if (!this.loginForm.valid || this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.username = this.loginForm.value.username;
    this.authService
      .login(
        this.username,
        this.loginForm.value.password
      )
      .subscribe({
        next: loginResult => {
          if (loginResult.mfaOption === PASSWORDLESS_SETUP_REQUIRED) {
            this.router.navigate([InternalRoutes.SETUP_PASSWORD],
              {queryParams: {username: this.username}}).then(() => this.isLoading = false);
          } else if (loginResult.mfaOption && loginResult.mfaOption !== DUPLICATE_USERNAME_SETUP_REQUIRED) {
            this.router.navigate([InternalRoutes.ENTER_VERIFICATION_CODE],
              {
                queryParams: {
                  mfaOption: loginResult.mfaOption,
                  username: this.username,
                  sessionId: loginResult.sessionId,
                  returnUrl: this.returnUrl
                },
                skipLocationChange: true
              }).then();
          } else {
            this.sessionService.loadSession().subscribe(data => {
              if (Object.keys(data).length > 0) {
                const returnUrl = loginResult.mfaOption === DUPLICATE_USERNAME_SETUP_REQUIRED
                  ? InternalRoutes.USERNAME_REMEDIATION : this.returnUrl;
                this.router.navigate([returnUrl]).then(() => this.isLoading = false);
              }
            });
          }
        },
        error: (error) => {
          this.isLoading = false;
          this.handleError(error);
        }
      });
  }

  resendConfirmationMail(): void {
    if (this.resendingMail) {
      return;
    }
    this.resendingMail = true;
    this.isLoading = true;

    this.authService.resendConfirmationMail(this.username)
      .subscribe({
        next: () => {
          this.displayNotification(LoginPageNotification.ResendConfirmationMailSuccess);
          this.isLoading = false;
          this.resendTimeout = setTimeout(() => this.resendingMail = false, 2000);
        },
        error: (error) => {
          this.handleError(error);
          this.isLoading = false;
          this.resendTimeout = setTimeout(() => this.resendingMail = false, 2000);
        }
      });
  }

  private checkAndDisplayNotifications(): void {
    this.clearNotification();

    const queryParams = this.route.snapshot.queryParamMap;
    if (queryParams.has('sessionExpired')) {
      this.displayNotification(LoginPageNotification.SessionExpired);
    } else if (queryParams.has('forgotPasswordChange')) {
      this.displayNotification(LoginPageNotification.ForgotPasswordChangeSuccess);
    } else if (queryParams.has('codeExpired')) {
      this.displayNotification(LoginPageNotification.CodeExpired);
    } else if (queryParams.has('userProfilePasswordChange')) {
      this.displayNotification(LoginPageNotification.UserProfilePasswordChangeSuccess);
    } else if (queryParams.has('limitExceededException')) {
      this.displayNotification(LoginPageNotification.LimitExceededException);
    } else if (queryParams.has('usernameRemediationChange')) {
      this.displayNotification(LoginPageNotification.UsernameRemediationSuccess);
    }
    window.scroll(0, 0);
  }

  private displayNotification(notification: LoginPageNotification): void {
    this.notification = notification;
  }

  private handleError(error: HttpErrorResponse): void {
    if (this.errorService.isInfrastructureError(error)) {
      this.notification = LoginPageNotification.TryAgainLater;
      window.scroll(0, 0);
      return;
    }

    switch (error?.error?.message) {
      case 'ERR_TOO_MANY_LOGIN_ATTEMPTS':
      case 'ERR_LIMIT_EXCEEDED':
        this.displayNotification(LoginPageNotification.LimitExceededException);
        break;
      case 'ERR_ACCESS_NOT_ALLOWED':
        this.notification = LoginPageNotification.WrongUsernameOrPassword;
        break;
      case 'ERR_REGISTRATION_NOT_CONFIRMED':
        this.displayNotification(LoginPageNotification.RegistrationNotConfirmed);
        break;
      case 'ERR_UNAVAILABLE_IN_MAINTENANCE':
        this.router.navigate(['/maintenance']).then();
        break;
      default:
        console.error('Error in login HTTP request:', error);
        this.router.navigate([InternalRoutes.ERROR]).then();
    }

    window.scroll(0, 0);
  }

  private clearNotification(): void {
    this.notification = undefined;
  }

  ngOnDestroy() {
    clearTimeout(this.resendTimeout);
  }
}
